import {DepGraph} from 'dependency-graph';
import {
  AccessMap,
  AccessRecord,
  AccessRecordLevel,
  DependencyMap,
  DependencyRecord,
  DependencyRegistrer,
  FieldAccessRecord,
  GroupAccessRecord,
  SectionAccessRecord,
} from '@/engine/types/dependencies';
import {DOMAIN_VALUE_PREFIX} from '@/model/types/constants';

export const createAccessMap = (): AccessMap => new Map<string, AccessRecord>();

const getAccessRecordLevel = (accessRecord: AccessRecord): AccessRecordLevel => {
  if ('fieldIndex' in accessRecord) return 'field';
  if ('groupIndex' in accessRecord) return 'group';
  if ('sectionIndex' in accessRecord) return 'section';
  if ('documentIndex' in accessRecord) return 'document';
  return 'unknown';
};

export const isFieldAccessRecord = (accessRecord: AccessRecord): accessRecord is FieldAccessRecord =>
  getAccessRecordLevel(accessRecord) === 'field';

export const isGroupAccessRecord = (accessRecord: AccessRecord): accessRecord is GroupAccessRecord =>
  getAccessRecordLevel(accessRecord) === 'group';

export const isSectionAccessRecord = (accessRecord: AccessRecord): accessRecord is SectionAccessRecord =>
  getAccessRecordLevel(accessRecord) === 'section';

export const isDocumentAccessRecord = (accessRecord: AccessRecord): accessRecord is SectionAccessRecord =>
  getAccessRecordLevel(accessRecord) === 'document';

export const createDependencyMap = (): DependencyMap => {
  return {
    map: new Map<string, DependencyRecord>(),
    graph: new DepGraph<string>({circular: true}),
  };
};

export const buildDependencyGraph = (dependencyMap: DependencyMap, parentId: string, children: string[]) => {
  dependencyMap.graph.addNode(parentId);
  for (const childId of children) {
    dependencyMap.graph.addNode(childId);
    dependencyMap.graph.addDependency(childId, parentId);
  }
};

export const createDependencyRegistrer =
  (dependencyMap: DependencyMap, id: string): DependencyRegistrer =>
  (category, ...dependencies: string[]) => {
    dependencies = dependencies.filter(dep => !dep.startsWith(DOMAIN_VALUE_PREFIX));
    const record = dependencyMap.map.get(id) ?? {
      all: new Set(),
      visibility: new Set(),
    };

    record[category] = new Set([...record[category], ...dependencies.map(dep => dep.replace(/#\{\$index\}|#\*/, '*'))]);
    if (category != 'all') {
      record.all = new Set([...record.all, ...dependencies.map(dep => dep.replace(/#\{\$index\}|#\*/, '*'))]);
    }

    dependencyMap.map.set(id, {...record});
    dependencyMap.graph.addNode(id);
    for (const dependency of dependencies) {
      dependencyMap.graph.addNode(dependency);
      if (dependency != id) dependencyMap.graph.addDependency(id, dependency);
    }
  };
